import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, Image as ImageIcon, X, Loader2 } from "lucide-react";
import { api, formatApiError } from "../lib/api";

/**
 * Reusable image uploader.
 *  - On change: posts the file to /api/media/upload, then calls onUploaded(url)
 *  - Renders a thumbnail when `value` is set (URL string)
 *  - Click to pick a file (drag-drop is a nice-to-have we can add later)
 *
 * Props:
 *   - value: string | null  current image URL
 *   - onUploaded: (url: string | null) => void
 *   - aspect: "square" | "wide"  (default "square")
 *   - testId: string
 *   - label: string
 */
export default function ImageUploader({ value, onUploaded, aspect = "square", testId = "img-uploader", label = "Image" }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const upload = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please pick an image file (PNG, JPEG, WebP, GIF, or SVG).");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error(`Image too large (${(file.size / 1024 / 1024).toFixed(1)} MB). Max is 5 MB.`);
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/media/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onUploaded(data.url);
      toast.success("Uploaded");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Upload failed");
    } finally { setBusy(false); }
  };

  const wrapperCls = aspect === "wide"
    ? "w-full h-32 rounded-lg overflow-hidden border-dashed border-2 border-white/10 flex items-center justify-center hover:border-[#00E676]/40 cursor-pointer"
    : "w-24 h-24 rounded-lg overflow-hidden border-dashed border-2 border-white/10 flex items-center justify-center hover:border-[#00E676]/40 cursor-pointer";

  return (
    <div className="space-y-2" data-testid={testId}>
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/gif,image/svg+xml"
        onChange={(e) => upload(e.target.files?.[0])}
        className="hidden"
        data-testid={`${testId}-input`}
      />
      {value ? (
        <div className="flex items-center gap-3">
          <img
            src={value}
            alt={label}
            className={aspect === "wide" ? "w-full h-32 object-cover rounded-lg" : "w-24 h-24 object-cover rounded-lg"}
            data-testid={`${testId}-preview`}
          />
          <div className="flex flex-col gap-1.5">
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              disabled={busy}
              data-testid={`${testId}-replace`}
              className="pp-chip text-xs px-3 py-1.5 flex items-center gap-1 disabled:opacity-50"
            >
              {busy ? <Loader2 size={11} className="animate-spin" /> : <Upload size={11} />} Replace
            </button>
            <button
              type="button"
              onClick={() => onUploaded(null)}
              disabled={busy}
              data-testid={`${testId}-remove`}
              className="px-3 py-1.5 rounded-lg text-xs border border-[#FF3B30]/30 text-[#FF3B30] hover:bg-[#FF3B30]/10 flex items-center gap-1 disabled:opacity-50"
            >
              <X size={11} /> Remove
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={busy}
          data-testid={`${testId}-pick`}
          className={wrapperCls}
        >
          <div className="flex flex-col items-center gap-1 text-zinc-500">
            {busy ? <Loader2 size={20} className="animate-spin" /> : <ImageIcon size={20} />}
            <span className="text-xs">{busy ? "Uploading…" : `Click to upload ${label}`}</span>
          </div>
        </button>
      )}
    </div>
  );
}
